/**
 * OffboardingBoard — 2-pane HR offboarding cockpit.
 *
 * URL contract:
 *   ?tab=offboarding&case=<workflowId>&section=<section>
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Circle,
  Clock,
  DollarSign,
  FileText,
  LogOut,
  Paperclip,
  Package,
  Plus,
  Search,
  Shield,
  Upload,
  User,
  X,
} from 'lucide-react';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import DatePickerField from '../../components/UI/DatePickerField';
import PillToggle from '../../components/UI/PillToggle';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { ClassificationBadge } from '../../components/UI/ClassificationBadge';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import ConfirmModal from '../../components/UI/ConfirmModal';
import {
  useCloseOffboardingCase,
  useCompleteOffboardingTask,
  useConfirmOffboardingDeparture,
  useEmergencyCutoff,
  useOffboardingCase,
  useOffboardingCases,
  usePreviewLeaveOffset,
  useProcessLeaveOffset,
  useRecordAgreedLeaveOffset,
  useRecordFullLeaveEncashment,
  useRecordIR21Exemption,
  useRecordIR21Filing,
  useRecordIR21Payment,
  useRecordOffboardingDeparture,
  useRecordWorkPassCancellation,
  useReleaseOffboardingPlan,
  useSetOffboardingDates,
  useUploadIR21DirectiveDocument,
  useConfirmIR21DirectiveFromDocument,
} from '../../hooks/useOffboardingCases';
import {
  formatDate,
  suggestEmploymentEndISO,
  parseLocalDate,
  toLocalISODate,
} from '../../utils/dateUtils';
import { formatCurrency, formatLeaveDays } from '../../utils/numberUtils';
import { confidentialAPI } from '../../services/api';
import IRASDirectiveVerificationModal from '../../components/IRASDirectiveVerificationModal';
import IRASCalculatorCheck from '../../components/Offboarding/IRASCalculatorCheck';
import {
  canSubmitVerifiedDirective,
  isReviewableIR21Document,
  payrollEffectCopy,
} from '../../utils/ir21DirectiveVerification';

const displayDate = (value) => (value ? formatDate(value) : '—');

const employmentBoundaryLabel = (value, session = 'PM') => {
  if (!value) return '—';
  return `${formatDate(value)} · after ${session === 'AM' ? 'morning' : 'afternoon'}`;
};

const accessCutoffLabel = (offboardingCase) => {
  if (offboardingCase?.access_cutoff_date) {
    return displayDate(offboardingCase.access_cutoff_date);
  }
  const lastDay = offboardingCase?.revised_employment_end_date || offboardingCase?.final_working_day;
  if (lastDay) {
    if (offboardingCase?.revised_employment_end_session === 'AM') {
      return `After morning session on ${formatDate(lastDay)}`;
    }
    return `After last shift on ${formatDate(lastDay)}`;
  }
  return 'After last shift on the last day';
};

const NOTICE_DATE_EXITS = new Set([
  'resignation',
  'mutual_agreement',
  'termination_with_notice',
]);

const FORM_DRIVEN_TASKS = new Set([
  'notice_and_dates',
  'leave_offset',
  'employee_communication',
  'repatriation',
  'departure_confirmation',
  'dorm_checkout',
  'work_pass_ir21',
  'work_pass_cancellation',
]);

const acceptIRASLetter = (file) => {
  if (!file) return null;
  if (file.size > 10 * 1024 * 1024) {
    toast.error('The letter must be 10 MB or smaller.');
    return null;
  }
  if (!/\.(pdf|jpe?g|png|webp)$/i.test(file.name)) {
    toast.error('Upload a PDF or image of the IRAS letter.');
    return null;
  }
  return file;
};

const acceptFlightTicket = (file) => {
  if (!file) return null;
  if (file.size > 10 * 1024 * 1024) {
    toast.error('The ticket must be 10 MB or smaller.');
    return null;
  }
  if (!/\.(pdf|jpe?g|png|webp)$/i.test(file.name)) {
    toast.error('Upload a PDF or image of the ticket.');
    return null;
  }
  return file;
};

const ir21HintCopy = (assessment) => {
  if (!assessment) return '';
  if (assessment.screening_status === 'required') {
    return assessment.evidence?.reasons?.length
      ? 'Employment dates and attendance evidence rule out an IRAS concession. File Form IR21 in myTax. If myTax returns an immediate notification that tax clearance is not required, record that outcome instead of filing.'
      : 'The recorded employment period does not meet an IRAS concession. File Form IR21 in myTax. If myTax returns an immediate notification that tax clearance is not required, record that outcome instead of filing.';
  }
  switch (assessment.exemption_hint) {
    case 'spr_lou':
      return 'Confirm whether this PR is leaving Singapore permanently. A Letter of Undertaking may apply.';
    case 'scenario_1':
      return 'A short-employment concession may apply. Verify occupation and prior Singapore employment in myTax.';
    case 'scenario_2':
      return 'A 183-day concession may apply. Processed payroll is not the final IR21 income figure; verify in myTax.';
    case 'scenario_3':
      return 'A two-year concession may apply. Verify physical presence, income for each year and prior employment in myTax.';
    case 'scenario_4':
      return 'A three-year concession may apply. Verify complete annual income and prior employment in myTax.';
    case 'citizen':
      return 'Singapore citizens do not file IR21.';
    default:
      return 'Verify whether tax clearance is required in myTax.';
  }
};

const ir21ExemptionCopy = (assessment) => {
  if (assessment?.screening_status === 'required') {
    return 'Processed payroll YTD below $21,000 does not waive Form IR21 on its own. Record this only when myTax or IRAS has confirmed that tax clearance is not required.';
  }
  return 'Use this when myTax or IRAS has confirmed that Form IR21 is not required. This closes the task and releases the payroll hold.';
};

const dormStayLabel = (stay) => {
  if (!stay) return '';
  const room = String(stay.room_number || '').trim();
  let bed = String(stay.bed_label || '').trim();
  if (room && bed.startsWith(`${room}-`)) {
    bed = bed.slice(room.length + 1);
  }
  const roomPart = room ? `Room ${room}` : '';
  const bedPart = bed ? `Bed ${bed}` : '';
  const roomBed = [roomPart, bedPart].filter(Boolean).join(' · ');
  return [stay.dorm_name, roomBed || stay.location_label].filter(Boolean).join(' · ');
};

const SECTION_LABELS = {
  dates: 'Dates',
  communication: 'Communication',
  leave: 'Leave',
  handover: 'Handover',
  interview: 'Interview',
  compliance: 'Compliance',
  operations: 'Operations',
  finance: 'Finance',
  assets: 'Assets',
  access: 'Access',
  close: 'Close',
};

const SECTION_ORDER = [
  'dates',
  'leave',
  'communication',
  'handover',
  'interview',
  'compliance',
  'operations',
  'finance',
  'assets',
  'access',
  'close',
];

const OffboardingBoard = ({ initialCaseId } = {}) => {
  const navigate = useNavigate();
  const { workflowId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('case') || initialCaseId || workflowId;
  const section = searchParams.get('section') || 'dates';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [playbookFilter, setPlaybookFilter] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endSession, setEndSession] = useState('');
  const [noticeDate, setNoticeDate] = useState('');
  const lastSuggestedEndRef = useRef('');
  const [cutoffReason, setCutoffReason] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmCutoff, setConfirmCutoff] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [confirmFullEncashment, setConfirmFullEncashment] = useState(false);
  const [confirmAgreedOffset, setConfirmAgreedOffset] = useState(false);
  const [confirmDeparture, setConfirmDeparture] = useState(false);
  const [confirmActualDeparture, setConfirmActualDeparture] = useState(false);
  const [confirmWorkPermitCardReturn, setConfirmWorkPermitCardReturn] = useState(false);
  const [confirmWorkPassCancellation, setConfirmWorkPassCancellation] = useState(false);
  const [confirmIR21Filing, setConfirmIR21Filing] = useState(false);
  const [confirmIR21Exemption, setConfirmIR21Exemption] = useState(false);
  const [showIr21Exemption, setShowIr21Exemption] = useState(false);
  const [confirmIR21Directive, setConfirmIR21Directive] = useState(false);
  const [confirmIR21Payment, setConfirmIR21Payment] = useState(false);
  const [departureDate, setDepartureDate] = useState('');
  const [actualDepartureDate, setActualDepartureDate] = useState('');
  const [flightTicket, setFlightTicket] = useState(null);
  const flightTicketInputRef = useRef(null);
  const [cancellationDate, setCancellationDate] = useState('');
  const [cancellationReference, setCancellationReference] = useState('');
  const [stayExpiryDate, setStayExpiryDate] = useState('');
  const [ir21SubmittedDate, setIr21SubmittedDate] = useState('');
  const [ir21FilingReference, setIr21FilingReference] = useState('');
  const [ir21ExemptionDate, setIr21ExemptionDate] = useState('');
  const [ir21ExemptionReference, setIr21ExemptionReference] = useState('');
  const [ir21ExemptionReason, setIr21ExemptionReason] = useState('');
  const [ir21PaidDate, setIr21PaidDate] = useState('');
  const [ir21VerifiedEvidence, setIr21VerifiedEvidence] = useState(null);
  const [ir21VerificationOpen, setIr21VerificationOpen] = useState(false);
  const [ir21ReviewDocument, setIr21ReviewDocument] = useState(null);
  const ir21LetterInputRef = useRef(null);
  const [agreedEndDate, setAgreedEndDate] = useState('');
  const [agreedEndSession, setAgreedEndSession] = useState('PM');
  const [agreedOffsetDays, setAgreedOffsetDays] = useState('');
  const [agreedOffsetPreview, setAgreedOffsetPreview] = useState(null);

  const {
    data: cases = [],
    isLoading,
    isError,
    refetch,
  } = useOffboardingCases({
    status: statusFilter === 'all' ? undefined : statusFilter,
    playbook: playbookFilter || undefined,
  });
  const { data: selected, isLoading: detailLoading, isError: detailError } = useOffboardingCase(selectedId);
  const releasePlan = useReleaseOffboardingPlan();
  const completeTask = useCompleteOffboardingTask();
  const closeCase = useCloseOffboardingCase();
  const setDates = useSetOffboardingDates();
  const previewOffset = usePreviewLeaveOffset();
  const processOffset = useProcessLeaveOffset();
  const recordAgreedOffset = useRecordAgreedLeaveOffset();
  const recordFullEncashment = useRecordFullLeaveEncashment();
  const recordDeparture = useRecordOffboardingDeparture();
  const confirmActualDepartureMutation = useConfirmOffboardingDeparture();
  const recordWorkPassCancellation = useRecordWorkPassCancellation();
  const recordIR21Filing = useRecordIR21Filing();
  const recordIR21Exemption = useRecordIR21Exemption();
  const uploadIR21Letter = useUploadIR21DirectiveDocument();
  const confirmIR21FromDocument = useConfirmIR21DirectiveFromDocument();
  const recordIR21Payment = useRecordIR21Payment();
  const emergencyCutoff = useEmergencyCutoff();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cases;
    return cases.filter((item) => {
      const name = `${item.employee?.first_name || ''} ${item.employee?.last_name || ''}`.toLowerCase();
      return (
        name.includes(term)
        || (item.employee_staff_id || '').toLowerCase().includes(term)
        || (item.exit_type || '').toLowerCase().includes(term)
      );
    });
  }, [cases, search]);

  const selectCase = (id, nextSection = section) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'offboarding');
    next.set('case', String(id));
    next.set('section', nextSection);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setEndDate('');
    setEndSession('');
    setNoticeDate('');
    setAgreedEndDate('');
    setAgreedEndSession('PM');
    setAgreedOffsetDays('');
    setAgreedOffsetPreview(null);
    setDepartureDate('');
    setActualDepartureDate('');
    setFlightTicket(null);
    setCancellationDate('');
    setCancellationReference('');
    setStayExpiryDate('');
    setIr21SubmittedDate('');
    setIr21FilingReference('');
    setIr21ExemptionDate('');
    setIr21ExemptionReference('');
    setIr21ExemptionReason('');
    setShowIr21Exemption(false);
    setIr21PaidDate('');
    setIr21VerifiedEvidence(null);
    setIr21VerificationOpen(false);
    setIr21ReviewDocument(null);
    lastSuggestedEndRef.current = '';
  }, [selectedId]);

  useEffect(() => {
    setIr21VerifiedEvidence(null);
  }, [selectedId, selected?.ir21_filing?.pending_document?.id]);

  const savedNoticeDate = selected?.notice_date || selected?.resignation_date || '';
  const effectiveNoticeDate = noticeDate || savedNoticeDate;
  const noticeMonths = Number(selected?.employee?.notice_period_months);
  const originalLocked = Boolean(selected?.original_employment_end_date);
  const suggestedEndDate = (
    effectiveNoticeDate
    && Number.isFinite(noticeMonths)
    && noticeMonths > 0
  ) ? suggestEmploymentEndISO(effectiveNoticeDate, noticeMonths) : '';

  useEffect(() => {
    if (!suggestedEndDate || originalLocked) return;
    if (!endDate || endDate === lastSuggestedEndRef.current) {
      setEndDate(suggestedEndDate);
      lastSuggestedEndRef.current = suggestedEndDate;
    }
  }, [suggestedEndDate, originalLocked, endDate]);

  useEffect(() => {
    if (!selectedId || detailLoading || !detailError) return;
    toast.info('That offboarding case is no longer available.');
    if (workflowId) {
      navigate('/admin/employee-lifecycle?tab=offboarding', { replace: true });
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('case');
    next.set('tab', 'offboarding');
    setSearchParams(next, { replace: true });
  }, [selectedId, detailLoading, detailError, workflowId, navigate, searchParams, setSearchParams]);

  const tasksForSection = (selected?.tasks || []).filter(
    (task) => task.section === section && task.applicable !== false,
  );
  const persistedEndDate = selected?.revised_employment_end_date || selected?.final_working_day || '';
  const persistedEndSession = (
    selected?.revised_employment_end_session
    || selected?.original_employment_end_session
    || 'PM'
  ).toUpperCase();
  const effectiveEndSession = (endSession || persistedEndSession).toUpperCase();
  const datesSaved = Boolean(selected?.original_employment_end_date);
  const datesDirty = Boolean(
    selected
    && (
      (NOTICE_DATE_EXITS.has(selected.exit_type) && effectiveNoticeDate !== savedNoticeDate)
      || (endDate || persistedEndDate) !== persistedEndDate
      || effectiveEndSession !== persistedEndSession
    )
  );
  const canSaveDates = Boolean(
    (endDate || persistedEndDate)
    && (!NOTICE_DATE_EXITS.has(selected?.exit_type) || effectiveNoticeDate)
  );
  const showSaveDates = canSaveDates && (!datesSaved || datesDirty);
  const lastDayRevised = Boolean(
    selected?.original_employment_end_date
    && persistedEndDate
    && selected.original_employment_end_date !== persistedEndDate
  );
  const showSuggestedLastDay = Boolean(
    suggestedEndDate && (!datesSaved || suggestedEndDate !== (endDate || persistedEndDate))
  );
  const planReleased = Boolean(selected?.employee_plan_released);
  const caseApprovalTask = (selected?.tasks || []).find(
    (task) => task.task_key === 'case_approval',
  );
  const caseApproved = Boolean(
    caseApprovalTask
    && ['completed', 'skipped', 'waived'].includes(caseApprovalTask.status),
  );
  const leaveOffsetTask = (selected?.tasks || []).find((task) => task.task_key === 'leave_offset');
  const leaveSettlementRequired = Boolean(leaveOffsetTask?.applicable);
  const leaveSettlementConfirmed = Boolean(
    !leaveSettlementRequired || selected?.leave_settlement?.confirmed,
  );
  const canRecordAgreedOffset = Boolean(
    leaveOffsetTask?.applicable
    && leaveOffsetTask.status === 'pending'
    && datesSaved
    && !lastDayRevised
    && !selected?.leave_offset_request
  );
  const offsetRequestStatus = selected?.leave_offset_request?.status || '';
  const offsetInFlight = ['proposed', 'manager_approved'].includes(offsetRequestStatus);
  const canSendToEmployee = Boolean(
    datesSaved
    && caseApproved
    && leaveSettlementConfirmed
    && !planReleased
    && !offsetInFlight
  );
  const sendBlockedReason = !datesSaved
    ? 'Save the employment end date on Dates first.'
    : !caseApproved
      ? 'Approve the saved dates on Dates first.'
      : !leaveSettlementConfirmed || offsetInFlight
        ? 'Confirm the annual-leave settlement on Leave first.'
        : '';
  const workPassNeedsFlight = selected?.ir21_assessment?.pass_type === 'wp';
  const workPassCancellationTask = (selected?.tasks || []).find(
    (task) => task.task_key === 'work_pass_cancellation',
  );
  const workPassCancellationRequired = Boolean(workPassCancellationTask?.applicable);
  const employmentHasEnded = Boolean(
    persistedEndDate && persistedEndDate <= toLocalISODate(new Date()),
  );
  const workPassCancellationReady = Boolean(
    employmentHasEnded
    && (!workPassNeedsFlight || (selected?.departure_date && selected?.has_flight_ticket)),
  );
  const ir21HoldActive = Boolean(
    selected?.requires_ir21
    && !['tax_paid', 'release', 'exempt'].includes(
      selected?.ir21_filing?.clearance_status,
    ),
  );
  const canCloseCase = Boolean(
    selected
    && selected.workflow_status !== 'completed'
    && (selected.blocking_open || []).length === 0
  );
  const sections = useMemo(() => {
    const available = new Set();
    (selected?.tasks || []).forEach((task) => {
      if (task.section && task.applicable !== false) available.add(task.section);
    });
    if (available.size === 0) return ['dates'];
    const known = SECTION_ORDER.filter((key) => available.has(key));
    const unknown = [...available].filter((key) => !SECTION_ORDER.includes(key));
    return [...known, ...unknown];
  }, [selected]);
  const completedSections = useMemo(() => {
    const completed = new Set();
    sections.forEach((key) => {
      const sectionTasks = (selected?.tasks || []).filter(
        (task) => task.section === key && task.applicable !== false,
      );
      if (
        sectionTasks.length > 0
        && sectionTasks.every((task) => ['completed', 'skipped', 'waived'].includes(task.status))
      ) {
        completed.add(key);
      }
    });
    return completed;
  }, [sections, selected]);

  const stats = useMemo(() => ({
    active: cases.filter((c) => ['initiated', 'in_progress'].includes(c.workflow_status)).length,
    worker: cases.filter((c) => c.playbook === 'worker' && ['initiated', 'in_progress'].includes(c.workflow_status)).length,
    staff: cases.filter((c) => c.playbook === 'staff' && ['initiated', 'in_progress'].includes(c.workflow_status)).length,
    blocked: cases.filter((c) => (c.blocking_open || []).length > 0).length,
  }), [cases]);

  return (
    <div className="flex flex-col xl:flex-row gap-4">
      <aside className="w-full xl:w-[340px] xl:flex-shrink-0 bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-gray-900">Offboarding cases</h2>
            <button
              type="button"
              className="btn btn-admin-primary btn-sm"
              onClick={() => navigate('/admin/employee-lifecycle/new-offboarding')}
              aria-label="New Offboarding"
              title="New Offboarding"
            >
              <Plus className="w-4 h-4" />
              New
            </button>
          </div>
          <DebouncedSearchInput
            value={search}
            onSearch={setSearch}
            placeholder="Search name or staff ID"
          />
          <PillToggle
            theme="admin"
            variant="text"
            fullWidth
            ariaLabel="Case status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { id: 'active', label: 'Active' },
              { id: 'all', label: 'All' },
            ]}
          />
          <PillToggle
            theme="admin"
            variant="text"
            fullWidth
            ariaLabel="Playbook"
            value={playbookFilter || 'any'}
            onChange={(id) => setPlaybookFilter(id === 'any' ? '' : id)}
            options={[
              { id: 'any', label: 'All types' },
              { id: 'worker', label: 'Worker' },
              { id: 'staff', label: 'Staff' },
            ]}
          />
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              ['Active', stats.active],
              ['Worker', stats.worker],
              ['Staff', stats.staff],
              ['Blocked', stats.blocked],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-gray-50 px-2 py-2">
                <p className="text-sm font-semibold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto divide-y">
          {isLoading && (
            <div className="py-12 flex justify-center"><LoadingSpinner /></div>
          )}
          {isError && (
            <div className="text-center py-12 px-4">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load cases</h3>
              <p className="text-sm text-gray-500">Check your connection and try again.</p>
              <button type="button" className="btn btn-admin-primary mt-4" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          )}
          {!isLoading && !isError && filtered.length === 0 && (
            <div className="text-center py-12 px-4">
              {search ? (
                <>
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No matching cases</h3>
                  <p className="text-sm text-gray-500">Try a different name or staff ID.</p>
                </>
              ) : (
                <>
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <LogOut className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No offboarding cases</h3>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto">
                    Start a worker or staff exit from New. HR owns the case until the employee plan is released.
                  </p>
                </>
              )}
            </div>
          )}
          {filtered.map((item) => {
            const active = String(item.id) === String(selectedId);
            const pct = item.progress?.percentage || 0;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectCase(item.id)}
                className={`w-full text-left p-4 hover:bg-red-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${active ? 'bg-red-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.employee?.first_name} {item.employee?.last_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.employee_staff_id}
                      {item.employee?.department ? ` · ${item.employee.department}` : ''}
                    </p>
                  </div>
                  <StatusBadge status={item.workflow_status} />
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {item.playbook && (
                    <ClassificationBadge classification={item.playbook === 'worker' ? 'worker' : 'staff'} />
                  )}
                  <span className="text-xs text-gray-500 capitalize">{(item.exit_type || '').replace(/_/g, ' ')}</span>
                  {(item.blocking_open || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                      <AlertTriangle className="w-3 h-3" />
                      {item.blocking_open.length} open
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Progress</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                    <div className="progress-bar-admin" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <section className="flex-1 min-w-0 bg-white rounded-2xl border shadow-sm min-h-[32rem]">
        {!selectedId && (
          <div className="text-center py-12 px-6">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a case</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Review dates, departmental sign-offs, payroll settlement and access cutoff from one workspace.
            </p>
          </div>
        )}
        {selectedId && detailLoading && (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        )}
        {selectedId && detailError && (
          <div className="text-center py-12 px-4">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load this case</h3>
            <p className="text-sm text-gray-500">It may have been withdrawn, or you may not have access.</p>
          </div>
        )}
        {selected && (
          <div>
            <header className="p-5 border-b">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-gray-900 truncate">
                    {selected.employee?.first_name} {selected.employee?.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selected.employee_staff_id} · {(selected.exit_type || '').replace(/_/g, ' ')}
                  </p>
                </div>
                {selected.originating_termination_request && (
                  <button
                    type="button"
                    className="btn btn-admin-outline btn-sm w-full sm:w-auto flex-shrink-0"
                    onClick={() => navigate(
                      `/admin/employee-lifecycle?tab=termination-requests&view=closed&id=${selected.originating_termination_request.id}`,
                    )}
                  >
                    <FileText className="w-4 h-4" aria-hidden="true" />
                    {selected.originating_termination_request.request_number || 'Open source request'}
                  </button>
                )}
              </div>
              <div className="mt-4 flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
                {sections.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectCase(selected.id, key)}
                    className={`tab-btn-admin ${section === key ? 'active' : ''}`}
                  >
                    {SECTION_LABELS[key] || key}
                    {completedSections.has(key) && (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                        <span className="sr-only">Complete</span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            </header>

            <div className="p-5 space-y-5">
              {section === 'dates' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {NOTICE_DATE_EXITS.has(selected.exit_type) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="resignation-date">
                        {selected.exit_type === 'termination_with_notice' ? 'Notice date' : 'Resignation date'}
                      </label>
                      <DatePickerField
                        id="resignation-date"
                        theme="admin"
                        selected={parseLocalDate(effectiveNoticeDate)}
                        onChange={(date) => setNoticeDate(toLocalISODate(date))}
                        placeholderText="Select date"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="employment-end-date">
                      Employment end date
                    </label>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                      <div className="min-w-0 flex-1">
                        <DatePickerField
                          id="employment-end-date"
                          theme="admin"
                          selected={parseLocalDate(endDate || persistedEndDate)}
                          onChange={(date) => setEndDate(toLocalISODate(date))}
                          placeholderText="Select date"
                        />
                      </div>
                      {showSaveDates && (
                        <button
                          type="button"
                          className="btn btn-admin-primary btn-sm w-full sm:w-auto flex-shrink-0"
                          disabled={setDates.isPending || !canSaveDates}
                          onClick={() => setDates.mutate({
                            id: selected.id,
                            data: {
                              employment_end_date: endDate || persistedEndDate,
                              employment_end_session: effectiveEndSession,
                              notice_date: effectiveNoticeDate || undefined,
                            },
                          })}
                        >
                          Save dates
                        </button>
                      )}
                    </div>
                    <div className="mt-3">
                      <span className="block text-sm font-medium text-gray-700 mb-2">
                        Employment ends
                      </span>
                      <PillToggle
                        theme="admin"
                        variant="text"
                        fullWidth
                        ariaLabel="Employment end session"
                        value={effectiveEndSession}
                        onChange={setEndSession}
                        options={[
                          { id: 'AM', label: 'After morning' },
                          { id: 'PM', label: 'After afternoon' },
                        ]}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 md:col-span-2">
                    {NOTICE_DATE_EXITS.has(selected.exit_type)
                      ? 'Enter the date on the letter. The last day is suggested from the contract notice period — change it if the letter already names a different last day. Do not subtract annual leave here.'
                      : 'Set the inclusive last day of employment. Do not subtract annual leave here.'}
                    {showSuggestedLastDay ? ` Suggested last day: ${formatDate(suggestedEndDate)}.` : ''}
                  </p>
                  <dl className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                    {selected.employee?.notice_period_months != null && selected.employee.notice_period_months !== '' && (
                      <div>
                        <dt className="text-xs text-gray-500">Contract notice</dt>
                        <dd>
                          {selected.employee.notice_period_months}
                          {' '}
                          {Number(selected.employee.notice_period_months) === 1 ? 'month' : 'months'}
                        </dd>
                      </div>
                    )}
                    {datesSaved && (
                      <div>
                        <dt className="text-xs text-gray-500">Access cutoff</dt>
                        <dd>{accessCutoffLabel(selected)}</dd>
                      </div>
                    )}
                    {lastDayRevised && (
                      <>
                        <div>
                          <dt className="text-xs text-gray-500">Letter last day</dt>
                          <dd>{displayDate(selected.original_employment_end_date)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Current last day</dt>
                          <dd>
                            {employmentBoundaryLabel(
                              persistedEndDate,
                              selected.revised_employment_end_session,
                            )}
                          </dd>
                        </div>
                      </>
                    )}
                    {selected.last_physical_work_date && (
                      <div>
                        <dt className="text-xs text-gray-500">Last physical work day</dt>
                        <dd>{displayDate(selected.last_physical_work_date)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {section === 'communication' && (
                <div className="rounded-xl border bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Final plan summary
                  </h3>
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
                    <div>
                      <dt className="text-xs text-gray-500">Employment ends</dt>
                      <dd className="font-medium text-gray-900">
                        {employmentBoundaryLabel(
                          persistedEndDate,
                          selected.revised_employment_end_session,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Annual leave</dt>
                      <dd className="font-medium text-gray-900">
                        {!leaveSettlementRequired
                          ? 'Not applicable'
                          : selected.leave_settlement?.preference === 'encashment'
                            ? 'Encash all remaining AL'
                            : leaveSettlementConfirmed
                              ? `${formatLeaveDays(
                                selected.leave_settlement?.offset_days || 0,
                              )} days notice offset`
                              : 'Not confirmed'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Tax clearance</dt>
                      <dd className="font-medium text-gray-900">
                        {selected.requires_ir21
                          ? ir21HoldActive ? 'IR21 hold applies' : 'IR21 resolved'
                          : 'Not required'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {tasksForSection.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks in this section</h3>
                  <p className="text-sm text-gray-500">Nothing is assigned here for this playbook.</p>
                </div>
              )}

              <ul className="space-y-3">
                {tasksForSection.map((task) => {
                  const done = ['completed', 'skipped', 'waived'].includes(task.status);
                  return (
                    <li key={task.id} className="rounded-xl border px-4 py-3 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {done ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{task.task_name}</p>
                            {!done && task.task_key === 'employee_communication' && sendBlockedReason && (
                              <p className="text-xs text-gray-500 mt-0.5">{sendBlockedReason}</p>
                            )}
                            {!done && task.description && !(task.task_key === 'employee_communication' && sendBlockedReason) && ![
                              'repatriation',
                              'departure_confirmation',
                              'dorm_checkout',
                              'work_pass_ir21',
                              'work_pass_cancellation',
                            ].includes(task.task_key) && (
                              <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                            )}
                            <p className="text-xs text-gray-400 capitalize">
                              {task.actor} · {task.status.replace(/_/g, ' ')}
                              {task.due_date ? ` · due ${formatDate(task.due_date)}` : ''}
                              {task.blocking ? ' · required' : ''}
                              {task.task_key === 'repatriation' && (
                                selected.departure_date || selected.special_pass_expiry_date
                              )
                                ? ` · ${formatDate(selected.departure_date || selected.special_pass_expiry_date)}`
                                : ''}
                              {task.task_key === 'dorm_checkout' && selected.dorm_checkout_date
                                ? ` · ${formatDate(selected.dorm_checkout_date)}`
                                : ''}
                            </p>
                          </div>
                        </div>
                        {task.task_key === 'employee_communication' && task.applicable && !done && (
                          <button
                            type="button"
                            className="btn btn-admin-primary btn-sm w-full sm:w-auto flex-shrink-0"
                            disabled={!canSendToEmployee || releasePlan.isPending}
                            onClick={() => setConfirmRelease(true)}
                          >
                            Send to employee
                          </button>
                        )}
                        {task.task_key === 'work_pass_ir21'
                          && task.applicable
                          && !done
                          && selected.ir21_filing?.clearance_status === 'not_started'
                          && !showIr21Exemption
                          && selected.ir21_assessment?.screening_status !== 'review_required' && (
                          <button
                            type="button"
                            className="btn btn-admin-outline btn-sm w-full sm:w-auto flex-shrink-0"
                            onClick={() => setShowIr21Exemption(true)}
                          >
                            Filing not required
                          </button>
                        )}
                        {task.applicable && !done && !FORM_DRIVEN_TASKS.has(task.task_key) && (task.actor === 'HR' || task.actor === 'Immigration') && (
                          <button
                            type="button"
                            className="btn btn-admin-outline btn-sm w-full sm:w-auto flex-shrink-0"
                            disabled={completeTask.isPending}
                            onClick={() => {
                              if (task.task_key === 'work_permit_card_return') {
                                setConfirmWorkPermitCardReturn(true);
                                return;
                              }
                              completeTask.mutate({ id: selected.id, taskKey: task.task_key });
                            }}
                          >
                            Complete
                          </button>
                        )}
                      </div>
                      {task.task_key === 'flight_readiness_acknowledgement' && done && (
                        <p className="sm:ml-8 text-xs text-green-700">
                          Worker confirmed the flight arrangements
                          {task.payload?.scheduled_departure_date
                            ? ` for ${formatDate(task.payload.scheduled_departure_date)}`
                            : ''}.
                        </p>
                      )}
                      {task.task_key === 'asset_return' && task.applicable && !done && (
                        <div className="sm:ml-8">
                          <button
                            type="button"
                            className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                            onClick={() => navigate(
                              `/asset-admin/checklists?tab=pending&type=offboarding&staff_id=${encodeURIComponent(selected.employee_staff_id)}`,
                            )}
                          >
                            <Package className="w-4 h-4" aria-hidden="true" />
                            Open asset checklist
                          </button>
                        </div>
                      )}
                      {task.task_key === 'work_pass_ir21' && task.applicable && !done && selected.ir21_assessment && (
                        <div className="sm:pl-8 space-y-3">
                          <IRASCalculatorCheck check={selected.ir21_assessment.calculator_check} />
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">
                              Portal recommendation
                            </span>
                            <StatusBadge
                              status={
                                selected.ir21_assessment.screening_status === 'required'
                                  ? 'due'
                                  : 'pending'
                              }
                              label={
                                selected.ir21_assessment.screening_status === 'required'
                                  ? 'Filing required'
                                  : 'Verify in myTax'
                              }
                            />
                            {selected.ir21_filing?.clearance_status === 'filed' && (
                              <StatusBadge status="pending" label="Awaiting IRAS directive" />
                            )}
                            {selected.ir21_filing?.clearance_status === 'pay_tax' && (
                              <StatusBadge status="pending_payment" label="Payment required" />
                            )}
                          </div>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl text-sm text-gray-700">
                            <div>
                              <dt className="text-xs text-gray-500">File IR21 by</dt>
                              <dd className={
                                selected.ir21_assessment.file_by_date
                                && selected.ir21_assessment.file_by_date < toLocalISODate(new Date())
                                  ? 'text-red-600'
                                  : undefined
                              }
                              >
                                {displayDate(selected.ir21_assessment.file_by_date)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Withhold monies from</dt>
                              <dd>{displayDate(selected.ir21_assessment.withhold_from_date)}</dd>
                            </div>
                          </dl>
                          <p className="text-xs text-gray-500">{ir21HintCopy(selected.ir21_assessment)}</p>
                          {Array.isArray(selected.ir21_assessment.evidence?.reasons)
                            && selected.ir21_assessment.evidence.reasons.length > 0 && (
                            <details className="max-w-xl rounded-lg border border-gray-200 px-3 py-2">
                              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                                Why this result?
                              </summary>
                              <ul className="pt-3 space-y-1 text-sm text-gray-700 list-disc pl-5">
                                {selected.ir21_assessment.evidence.reasons.map((reason) => (
                                  <li key={reason}>{reason}</li>
                                ))}
                              </ul>
                              <div className="pt-3 flex flex-wrap gap-x-4 gap-y-1">
                                <a
                                  className="text-sm font-medium text-red-600 hover:text-red-700"
                                  href={`/admin/employees?tab=professional&employee=${encodeURIComponent(selected.employee_staff_id || '')}&subTab=experience`}
                                >
                                  Experience
                                </a>
                                <a
                                  className="text-sm font-medium text-red-600 hover:text-red-700"
                                  href="/admin/attendance"
                                >
                                  Attendance
                                </a>
                              </div>
                            </details>
                          )}
                          {selected.ir21_filing?.clearance_status === 'not_started' && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ir21-submitted-date">
                                    Submission date
                                  </label>
                                  <DatePickerField
                                    id="ir21-submitted-date"
                                    theme="admin"
                                    wrapperClassName="w-full"
                                    selected={parseLocalDate(ir21SubmittedDate)}
                                    onChange={(value) => setIr21SubmittedDate(toLocalISODate(value))}
                                    maxDate={new Date()}
                                    placeholderText="Select date"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ir21-filing-reference">
                                    myTax acknowledgement
                                  </label>
                                  <input
                                    id="ir21-filing-reference"
                                    type="text"
                                    className="input-admin"
                                    value={ir21FilingReference}
                                    onChange={(event) => setIr21FilingReference(event.target.value)}
                                    placeholder="Enter reference"
                                  />
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <a
                                  className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                                  href="https://mytax.iras.gov.sg/"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open myTax
                                </a>
                                <button
                                  type="button"
                                  className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                                  disabled={!ir21SubmittedDate || !ir21FilingReference.trim()}
                                  onClick={() => setConfirmIR21Filing(true)}
                                >
                                  Record filing
                                </button>
                              </div>
                              {(
                                showIr21Exemption
                                || selected.ir21_assessment.screening_status === 'review_required'
                              ) && (
                                <div className="max-w-xl rounded-xl border border-gray-200 px-4 py-3 space-y-3">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">Filing not required</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {ir21ExemptionCopy(selected.ir21_assessment)}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ir21-exemption-date">
                                        Verified date
                                      </label>
                                      <DatePickerField
                                        id="ir21-exemption-date"
                                        theme="admin"
                                        wrapperClassName="w-full"
                                        selected={parseLocalDate(ir21ExemptionDate)}
                                        onChange={(value) => setIr21ExemptionDate(toLocalISODate(value))}
                                        maxDate={new Date()}
                                        placeholderText="Select date"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ir21-exemption-reference">
                                        myTax / IRAS reference
                                      </label>
                                      <input
                                        id="ir21-exemption-reference"
                                        type="text"
                                        className="input-admin"
                                        value={ir21ExemptionReference}
                                        onChange={(event) => setIr21ExemptionReference(event.target.value)}
                                        placeholder="Enter reference"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ir21-exemption-reason">
                                      Verified reason
                                    </label>
                                    <textarea
                                      id="ir21-exemption-reason"
                                      className="textarea-admin w-full"
                                      rows={2}
                                      value={ir21ExemptionReason}
                                      onChange={(event) => setIr21ExemptionReason(event.target.value)}
                                      placeholder="Record the myTax notification or IRAS concession"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                                    disabled={
                                      !ir21ExemptionDate
                                      || !ir21ExemptionReference.trim()
                                      || !ir21ExemptionReason.trim()
                                    }
                                    onClick={() => setConfirmIR21Exemption(true)}
                                  >
                                    Record verified exemption
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {selected.ir21_filing?.clearance_status === 'filed' && (
                            <div className="space-y-3">
                              <p className="text-sm text-gray-700">
                                Filed {displayDate(selected.ir21_filing.submitted_date)}
                                {' · '}
                                {selected.ir21_filing.reference}
                              </p>
                              <p className="text-sm text-gray-600">
                                Upload the IRAS letter. OCR proposes values; you must verify them against the document before the outcome is recorded.
                              </p>
                              <input
                                ref={ir21LetterInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={(event) => {
                                  const file = acceptIRASLetter(event.target.files?.[0]);
                                  event.target.value = '';
                                  if (!file) return;
                                  uploadIR21Letter.mutate(
                                    { id: selected.id, file },
                                    {
                                      onSuccess: (response) => {
                                        setIr21VerifiedEvidence(null);
                                        setIr21ReviewDocument(response?.data?.document || null);
                                        setIr21VerificationOpen(true);
                                      },
                                    },
                                  );
                                }}
                              />
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  type="button"
                                  className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                                  disabled={uploadIR21Letter.isPending}
                                  onClick={() => ir21LetterInputRef.current?.click()}
                                >
                                  <Upload className="w-4 h-4 mr-2" />
                                  {uploadIR21Letter.isPending ? 'Uploading…' : 'Upload IRAS outcome'}
                                </button>
                                {isReviewableIR21Document(
                                  ir21ReviewDocument || selected.ir21_filing?.pending_document
                                ) && (
                                  <button
                                    type="button"
                                    className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                                    onClick={() => {
                                      setIr21ReviewDocument(
                                        ir21ReviewDocument || selected.ir21_filing?.pending_document,
                                      );
                                      setIr21VerificationOpen(true);
                                    }}
                                  >
                                    Review uploaded outcome
                                  </button>
                                )}
                              </div>
                              {selected.ir21_filing?.pending_document && (
                                <p className="text-xs text-gray-500">
                                  {selected.ir21_filing.pending_document.original_filename}
                                  {' · '}
                                  <StatusBadge
                                    status={selected.ir21_filing.pending_document.verification_status}
                                  />
                                </p>
                              )}
                              {ir21VerifiedEvidence && (
                                <p className="text-sm text-gray-700">
                                  Verified
                                  {' '}
                                  {ir21VerifiedEvidence.outcome === 'pay_tax' ? 'Directive to Pay Tax' : 'Notification to Release Monies'}
                                  {ir21VerifiedEvidence.amount ? ` · SGD ${ir21VerifiedEvidence.amount}` : ''}
                                </p>
                              )}
                              <button
                                type="button"
                                className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                                disabled={!canSubmitVerifiedDirective(ir21VerifiedEvidence)}
                                onClick={() => setConfirmIR21Directive(true)}
                              >
                                Record directive
                              </button>
                            </div>
                          )}
                          {selected.ir21_filing?.clearance_status === 'pay_tax' && (
                            <div className="space-y-3">
                              <p className="text-sm text-gray-700">
                                {formatCurrency(selected.ir21_filing.directive_amount, { grouped: true })}
                                {' due '}
                                {displayDate(selected.ir21_filing.directive_due_date)}
                              </p>
                              {selected.ir21_filing.payroll_recovery_status === 'manual_recovery' && (
                                <div className="border border-red-200 bg-red-50 rounded-xl p-3 text-sm text-red-800">
                                  Final salary was already paid. Finance must recover this amount from the employee. Recording IRAS payment does not create a payroll deduction.
                                </div>
                              )}
                              {selected.ir21_filing.payroll_recovery_status === 'pending' && (
                                <p className="text-sm text-gray-600">
                                  Payroll will deduct this amount from the withheld final payment once. Paying IRAS does not by itself complete employee recovery.
                                </p>
                              )}
                              <div className="w-full sm:w-56">
                                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ir21-paid-date">
                                  Paid to IRAS
                                </label>
                                <DatePickerField
                                  id="ir21-paid-date"
                                  theme="admin"
                                  wrapperClassName="w-full"
                                  selected={parseLocalDate(ir21PaidDate)}
                                  onChange={(value) => setIr21PaidDate(toLocalISODate(value))}
                                  maxDate={new Date()}
                                  placeholderText="Select date"
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                                disabled={!ir21PaidDate}
                                onClick={() => setConfirmIR21Payment(true)}
                              >
                                Record payment
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {task.task_key === 'work_pass_cancellation' && task.applicable && !done && (
                        <div className="sm:pl-8 space-y-3">
                          <p className="text-xs text-gray-500">
                            {!employmentHasEnded
                              ? `Available after employment ends on ${displayDate(persistedEndDate)}.`
                              : workPassNeedsFlight && (!selected.departure_date || !selected.has_flight_ticket)
                                ? 'Book the flight in Operations before cancelling the Work Permit.'
                                : selected.ir21_assessment?.pass_type === 'wp'
                                  ? 'Record the MOM cancellation and the expiry printed on the Special Pass.'
                                  : 'Record the MOM cancellation and STVP expiry if the pass holder remains in Singapore.'}
                          </p>
                          {employmentHasEnded
                            && workPassNeedsFlight
                            && (!selected.departure_date || !selected.has_flight_ticket) && (
                            <button
                              type="button"
                              className="text-sm font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded"
                              onClick={() => selectCase(selected.id, 'operations')}
                            >
                              Open Operations
                            </button>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="work-pass-cancellation-date">
                                Cancellation date
                              </label>
                              <DatePickerField
                                id="work-pass-cancellation-date"
                                theme="admin"
                                wrapperClassName="w-full"
                                selected={parseLocalDate(cancellationDate)}
                                onChange={(value) => setCancellationDate(toLocalISODate(value))}
                                placeholderText="Effective date"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="work-pass-reference">
                                MOM reference
                              </label>
                              <input
                                id="work-pass-reference"
                                type="text"
                                className="input-admin"
                                value={cancellationReference}
                                onChange={(event) => setCancellationReference(event.target.value)}
                                placeholder="Enter transaction reference"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="stay-expiry-date">
                                {selected.ir21_assessment?.pass_type === 'wp'
                                  ? 'Special Pass expiry'
                                  : 'STVP expiry'}
                              </label>
                              <DatePickerField
                                id="stay-expiry-date"
                                theme="admin"
                                wrapperClassName="w-full"
                                selected={parseLocalDate(stayExpiryDate)}
                                onChange={(value) => setStayExpiryDate(toLocalISODate(value))}
                                placeholderText="Select date"
                              />
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <a
                              className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                              href={
                                selected.ir21_assessment?.pass_type === 'wp'
                                  ? 'https://www.mom.gov.sg/passes-and-permits/work-permit-for-foreign-worker/cancel-a-work-permit'
                                  : 'https://www.mom.gov.sg/passes-and-permits/s-pass/cancel-a-pass'
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open MOM instructions
                            </a>
                            <button
                              type="button"
                              className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                              disabled={
                                !workPassCancellationReady
                                || !cancellationDate
                                || !cancellationReference.trim()
                                || !stayExpiryDate
                              }
                              onClick={() => setConfirmWorkPassCancellation(true)}
                            >
                              Record cancellation
                            </button>
                          </div>
                        </div>
                      )}
                      {task.task_key === 'departure_confirmation' && task.applicable && !done && (
                        <div className="sm:pl-8 space-y-3">
                          {workPassCancellationRequired && !selected.work_pass_cancellation?.cancelled ? (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500">
                                Record work-pass cancellation in Compliance before confirming departure.
                              </p>
                              <button
                                type="button"
                                className="text-sm font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded"
                                onClick={() => selectCase(selected.id, 'compliance')}
                              >
                                Open Compliance
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-700">
                                Expected {displayDate(selected.departure_date)}
                                {' · Pass cancelled '}
                                {displayDate(selected.work_pass_cancellation.cancellation_date)}
                              </p>
                              <div className="w-full sm:w-56">
                                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="actual-departure-date">
                                  Actual departure date
                                </label>
                                <DatePickerField
                                  id="actual-departure-date"
                                  theme="admin"
                                  wrapperClassName="w-full"
                                  selected={parseLocalDate(actualDepartureDate)}
                                  onChange={(value) => setActualDepartureDate(toLocalISODate(value))}
                                  maxDate={new Date()}
                                  placeholderText="Select date"
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                                disabled={!actualDepartureDate}
                                onClick={() => setConfirmActualDeparture(true)}
                              >
                                Confirm departure
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      {task.task_key === 'dorm_checkout' && task.applicable && !done && (
                        <div className="sm:pl-8 flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="min-w-0 flex-1">
                            {selected.dorm_stay?.occupied && dormStayLabel(selected.dorm_stay) && (
                              <p className="text-sm text-gray-700">
                                {[selected.employee_staff_id, dormStayLabel(selected.dorm_stay)].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-0.5">
                              {selected.departure_date || selected.special_pass_expiry_date
                                ? `Enter ${formatDate(selected.departure_date || selected.special_pass_expiry_date)} in the Dormitory Portal — this row then closes.`
                                : 'Record move-out in the Dormitory Portal — this row then closes.'}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-admin-outline btn-sm w-full sm:w-auto flex-shrink-0"
                            onClick={() => navigate(
                              `/admin/dormitory-management?tab=assignments&q=${encodeURIComponent(selected.employee_staff_id || '')}`,
                            )}
                          >
                            <Building2 className="w-4 h-4" />
                            Open Assignments
                          </button>
                        </div>
                      )}
                      {task.task_key === 'repatriation' && task.applicable && !done && (
                        <div className="sm:pl-8 space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                            <div className="w-full sm:w-56">
                              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="departure-date">
                                Scheduled flight date
                              </label>
                              <DatePickerField
                                id="departure-date"
                                theme="admin"
                                wrapperClassName="w-full"
                                selected={parseLocalDate(departureDate)}
                                onChange={(date) => setDepartureDate(toLocalISODate(date))}
                                placeholderText="Select date"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="block text-sm font-medium text-gray-700 mb-2">Flight ticket</p>
                              <div className="flex items-center gap-2 min-w-0">
                                <button
                                  type="button"
                                  className="btn btn-admin-outline btn-sm flex-shrink-0"
                                  onClick={() => flightTicketInputRef.current?.click()}
                                >
                                  <Upload className="w-4 h-4" />
                                  {flightTicket ? 'Replace ticket' : 'Upload ticket'}
                                </button>
                                <input
                                  ref={flightTicketInputRef}
                                  type="file"
                                  accept="application/pdf,image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={(event) => {
                                    const next = acceptFlightTicket(event.target.files?.[0]);
                                    if (next) setFlightTicket(next);
                                    event.target.value = '';
                                  }}
                                />
                                {flightTicket && (
                                  <div className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
                                    <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{flightTicket.name}</span>
                                    <button
                                      type="button"
                                      className="icon-button-admin"
                                      aria-label="Remove ticket"
                                      onClick={() => setFlightTicket(null)}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-admin-primary btn-sm w-full sm:w-auto"
                            disabled={!departureDate || !flightTicket || recordDeparture.isPending}
                            onClick={() => setConfirmDeparture(true)}
                          >
                            Record arrangements
                          </button>
                        </div>
                      )}
                      {task.task_key === 'repatriation' && done && selected.has_flight_ticket && (
                        <div className="sm:pl-8">
                          <button
                            type="button"
                            className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                            onClick={async () => {
                              try {
                                const response = await confidentialAPI.downloadOffboardingFlightTicket(selected.id);
                                const blob = new Blob([response.data]);
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = selected.flight_ticket_filename || 'flight_ticket';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch {
                                toast.error('Unable to download the flight ticket');
                              }
                            }}
                          >
                            <Paperclip className="w-4 h-4" />
                            {selected.flight_ticket_filename || 'Download ticket'}
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {section === 'finance' && (
                <div className="rounded-xl bg-gray-50 border p-4 space-y-3">
                  <p className="text-sm text-gray-700">
                    Final settlement is calculated in Payroll.
                    {ir21HoldActive
                      ? ' It can be prepared now, but payment remains blocked until IR21 clearance is resolved.'
                      : ' After Confirm & Process, this financial task closes automatically.'}
                  </p>
                  {ir21HoldActive && (
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded"
                      onClick={() => selectCase(selected.id, 'compliance')}
                    >
                      Review IR21 clearance
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-admin-outline btn-sm"
                    onClick={() => navigate('/admin/payroll?tab=final-settlements')}
                  >
                    <DollarSign className="w-4 h-4" />
                    Open Final Settlements
                  </button>
                </div>
              )}
              {section === 'access' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    Portal access stays open through the last shift. The scheduled job turns it off that evening after hours — not the next calendar day. Use emergency cutoff only when waiting is not safe.
                  </div>
                  <input
                    type="text"
                    className="input-admin"
                    value={cutoffReason}
                    onChange={(e) => setCutoffReason(e.target.value)}
                    placeholder="Reason for emergency cutoff"
                    aria-label="Reason for emergency cutoff"
                  />
                  <button
                    type="button"
                    className="btn btn-admin-outline btn-sm"
                    disabled={!cutoffReason.trim()}
                    onClick={() => setConfirmCutoff(true)}
                  >
                    Apply emergency cutoff
                  </button>
                </div>
              )}
              {section === 'leave' && (
                <div className="space-y-3">
                  {leaveSettlementRequired
                    && !leaveSettlementConfirmed
                    && datesSaved
                    && !offsetInFlight && (
                    <div className="rounded-xl border bg-white p-4 space-y-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          Confirm annual-leave treatment
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Settle this before the final plan is sent. If the employee works to the letter date,
                          all remaining AL goes to Final Settlement.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                        disabled={recordFullEncashment.isPending}
                        onClick={() => setConfirmFullEncashment(true)}
                      >
                        Encash all remaining AL
                      </button>
                    </div>
                  )}
                  {selected.leave_settlement?.preference === 'encashment' && (
                    <div className="rounded-xl border bg-white p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            Annual leave confirmed
                          </h3>
                          <p className="text-sm text-gray-700 mt-1">
                            Encash all remaining AL · employment ends{' '}
                            {employmentBoundaryLabel(
                              persistedEndDate,
                              selected.revised_employment_end_session,
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Projected encashment: {formatLeaveDays(
                              selected.leave_settlement.encash_days || 0,
                            )} days. Payroll recalculates the final balance at settlement.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {canRecordAgreedOffset && (
                    <div className="rounded-xl bg-gray-50 border p-4 space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          Bring the last day forward with AL
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Enter the AL days agreed with the employee and reviewer, in 0.5-day work sessions.
                          The last day is counted back from the letter last day on scheduled work days — not by subtracting calendar dates.
                          The preview shows whether the remaining AL is encashed or the maximum eligible offset is used.
                        </p>
                      </div>
                      <div className="max-w-md space-y-4">
                        <div>
                          <label
                            className="block text-sm font-medium text-gray-700 mb-2"
                            htmlFor="agreed-offset-days"
                          >
                            AL days to use
                          </label>
                          <input
                            id="agreed-offset-days"
                            type="number"
                            min="0.5"
                            step="0.5"
                            className="input-admin"
                            value={agreedOffsetDays}
                            onChange={(event) => {
                              setAgreedOffsetDays(event.target.value);
                              setAgreedOffsetPreview(null);
                            }}
                            placeholder="e.g. 1.5"
                          />
                        </div>
                        <div>
                          <label
                            className="block text-sm font-medium text-gray-700 mb-2"
                            htmlFor="agreed-employment-end-date"
                          >
                            Or agreed last day
                          </label>
                          <DatePickerField
                            id="agreed-employment-end-date"
                            theme="admin"
                            selected={parseLocalDate(agreedEndDate)}
                            onChange={(date) => {
                              setAgreedEndDate(toLocalISODate(date));
                              setAgreedOffsetDays('');
                              setAgreedOffsetPreview(null);
                            }}
                            maxDate={parseLocalDate(selected.original_employment_end_date)}
                            placeholderText="Select revised date"
                          />
                          <div className="mt-3">
                            <span className="block text-sm font-medium text-gray-700 mb-2">
                              Employment ends
                            </span>
                            <PillToggle
                              theme="admin"
                              variant="text"
                              fullWidth
                              ariaLabel="Final employment session"
                              value={agreedEndSession}
                              onChange={(session) => {
                                setAgreedEndSession(session);
                                setAgreedOffsetDays('');
                                setAgreedOffsetPreview(null);
                              }}
                              options={[
                                { id: 'AM', label: 'After morning' },
                                { id: 'PM', label: 'After afternoon' },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="btn btn-admin-outline btn-sm"
                          disabled={
                            previewOffset.isPending
                            || !(
                              (Number(agreedOffsetDays) > 0 && Number(agreedOffsetDays) % 0.5 === 0)
                              || agreedEndDate
                            )
                          }
                          onClick={async () => {
                            try {
                              const useDays = (
                                Number(agreedOffsetDays) > 0
                                && Number(agreedOffsetDays) % 0.5 === 0
                              );
                              const response = await previewOffset.mutateAsync({
                                id: selected.id,
                                data: useDays
                                  ? { offset_days: agreedOffsetDays }
                                  : {
                                    revised_end_date: agreedEndDate,
                                    revised_end_session: agreedEndSession,
                                  },
                              });
                              const preview = response.data?.preview || null;
                              setAgreedOffsetPreview(preview);
                              if (preview?.revised_end_date) {
                                setAgreedEndDate(preview.revised_end_date);
                                setAgreedEndSession(preview.revised_end_session || 'PM');
                                setAgreedOffsetDays(
                                  formatLeaveDays(preview.proposed_offset_days || agreedOffsetDays)
                                );
                              }
                            } catch (error) {
                              const details = error.response?.data?.error || {};
                              toast.error(
                                details.revised_end_date
                                || details.offset_days
                                || 'Could not validate this offset',
                              );
                            }
                          }}
                        >
                          Preview AL impact
                        </button>
                      </div>
                      {agreedOffsetPreview && (
                        <div className="rounded-xl border bg-white p-4 space-y-3">
                          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div>
                              <dt className="text-xs text-gray-500">AL used for notice</dt>
                              <dd className="font-medium text-gray-900">
                                {formatLeaveDays(agreedOffsetPreview.proposed_offset_days)} days
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Actual employment boundary</dt>
                              <dd className="font-medium text-gray-900">
                                {displayDate(agreedOffsetPreview.revised_end_date)}
                                {' · '}
                                {agreedOffsetPreview.revised_end_session === 'AM'
                                  ? 'after morning'
                                  : 'after afternoon'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Remaining for encashment</dt>
                              <dd className="font-medium text-gray-900">
                                {formatLeaveDays(agreedOffsetPreview.remaining_encash_days)} days
                              </dd>
                            </div>
                          </dl>
                          <button
                            type="button"
                            className="btn btn-admin-primary btn-sm"
                            onClick={() => setConfirmAgreedOffset(true)}
                          >
                            Apply agreed end date
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {selected.leave_offset_request && (
                    <div className="rounded-xl border bg-white p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {selected.leave_offset_request.status === 'processed'
                            ? 'Notice offset applied'
                            : selected.leave_offset_request.status === 'manager_approved'
                              ? 'Offset ready to apply'
                              : 'Offset waiting for reviewer'}
                        </h3>
                        {selected.leave_offset_request.status !== 'processed' && (
                          <StatusBadge
                            status={
                              selected.leave_offset_request.status === 'manager_approved'
                                ? 'approved'
                                : 'pending'
                            }
                            label={
                              selected.leave_offset_request.status === 'manager_approved'
                                ? 'Ready to apply'
                                : 'Waiting for reviewer'
                            }
                          />
                        )}
                      </div>
                      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        <div>
                          <dt className="text-xs text-gray-500">AL used for notice</dt>
                          <dd className="font-medium text-gray-900">
                            {formatLeaveDays(selected.leave_offset_request.proposed_offset_days)} days
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Actual last day</dt>
                          <dd className="font-medium text-gray-900">
                            {employmentBoundaryLabel(
                              selected.leave_offset_request.revised_end_date,
                              selected.leave_offset_request.revised_end_session,
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Remaining for encashment</dt>
                          <dd className="font-medium text-gray-900">
                            {formatLeaveDays(selected.leave_offset_request.remaining_encash_days)} days
                          </dd>
                        </div>
                      </dl>
                      {selected.leave_offset_request.status === 'manager_approved' && (
                        <button
                          type="button"
                          className="btn btn-admin-primary btn-sm"
                          disabled={processOffset.isPending}
                          onClick={() => processOffset.mutate({
                            requestId: selected.leave_offset_request.id,
                            data: {},
                          })}
                        >
                          Validate & Process
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {section === 'close' && (
                <div className="space-y-3">
                  {(selected.blocking_open || []).length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-orange-700">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      Required tasks still open: {selected.blocking_open.join(', ')}
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-admin-primary btn-sm"
                    disabled={!canCloseCase}
                    onClick={() => setConfirmClose(true)}
                  >
                    Close case
                  </button>
                  <button
                    type="button"
                    className="btn btn-admin-outline btn-sm"
                    onClick={() => navigate(`/admin/employee-lifecycle/offboarding/${selected.id}/step-${selected.current_step || 1}`)}
                  >
                    Open legacy wizard
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      <ConfirmModal
        isOpen={confirmFullEncashment}
        theme="admin"
        title="Confirm full annual-leave encashment?"
        message={
          persistedEndDate
            ? `The employee will work to ${employmentBoundaryLabel(
              persistedEndDate,
              selected?.revised_employment_end_session,
            )}. Remaining annual leave will be recalculated and paid through Final Settlement.`
            : ''
        }
        confirmLabel="Confirm encashment"
        variant="primary"
        isLoading={recordFullEncashment.isPending}
        onCancel={() => setConfirmFullEncashment(false)}
        onConfirm={() => {
          recordFullEncashment.mutate(selected?.id, {
            onSettled: () => setConfirmFullEncashment(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmRelease}
        theme="admin"
        title={
          selected?.employee?.first_name
            ? `Send offboarding details to ${selected.employee.first_name}?`
            : 'Send offboarding details to the employee?'
        }
        message="This releases the confirmed employment end date, annual-leave treatment and assigned tasks to Self-Service Offboarding. Later date changes must be communicated separately."
        confirmLabel="Send to employee"
        variant="primary"
        isLoading={releasePlan.isPending}
        onCancel={() => setConfirmRelease(false)}
        onConfirm={() => {
          releasePlan.mutate(selected?.id, {
            onSettled: () => setConfirmRelease(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmDeparture}
        theme="admin"
        title={
          selected?.employee?.first_name
            ? `Record ${selected.employee.first_name}'s flight arrangements?`
            : 'Record repatriation arrangements?'
        }
        message={
          departureDate
            ? `This stores the ticket and scheduled flight on ${formatDate(departureDate)}. It does not cancel the work pass, confirm departure, or check anyone out of a dorm.`
            : ''
        }
        confirmLabel="Record arrangements"
        variant="primary"
        isLoading={recordDeparture.isPending}
        onCancel={() => setConfirmDeparture(false)}
        onConfirm={() => {
          const data = new FormData();
          data.append('departure_date', departureDate);
          data.append('special_pass_expiry_date', departureDate);
          if (flightTicket) data.append('flight_ticket', flightTicket);
          recordDeparture.mutate({
            id: selected?.id,
            data,
          }, {
            onSuccess: () => {
              setDepartureDate('');
              setFlightTicket(null);
            },
            onSettled: () => setConfirmDeparture(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmActualDeparture}
        theme="admin"
        title="Confirm actual departure?"
        message={
          actualDepartureDate
            ? `Confirm that the worker left Singapore on ${formatDate(actualDepartureDate)}. This does not post dormitory move-out.`
            : ''
        }
        confirmLabel="Confirm departure"
        variant="primary"
        isLoading={confirmActualDepartureMutation.isPending}
        onCancel={() => setConfirmActualDeparture(false)}
        onConfirm={() => {
          confirmActualDepartureMutation.mutate({
            id: selected?.id,
            data: { actual_departure_date: actualDepartureDate },
          }, {
            onSuccess: () => setActualDepartureDate(''),
            onSettled: () => setConfirmActualDeparture(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmWorkPermitCardReturn}
        theme="admin"
        title="Confirm Work Permit card return?"
        message={
          selected
            ? `Confirm that ${selected.employee?.first_name || 'the worker'}’s physical Work Permit card has been received. This does not record MOM cancellation.`
            : ''
        }
        confirmLabel="Confirm receipt"
        variant="primary"
        isLoading={completeTask.isPending}
        onCancel={() => setConfirmWorkPermitCardReturn(false)}
        onConfirm={() => completeTask.mutate(
          { id: selected?.id, taskKey: 'work_permit_card_return' },
          { onSettled: () => setConfirmWorkPermitCardReturn(false) },
        )}
      />
      <ConfirmModal
        isOpen={confirmWorkPassCancellation}
        theme="admin"
        title="Record work-pass cancellation?"
        message={
          cancellationDate
            ? `This records the effective MOM cancellation on ${formatDate(cancellationDate)}. It does not cancel the pass in MOM.`
            : ''
        }
        confirmLabel="Record cancellation"
        variant="primary"
        isLoading={recordWorkPassCancellation.isPending}
        onCancel={() => setConfirmWorkPassCancellation(false)}
        onConfirm={() => {
          recordWorkPassCancellation.mutate({
            id: selected?.id,
            data: {
              cancellation_date: cancellationDate,
              reference: cancellationReference.trim(),
              stay_document: selected?.ir21_assessment?.pass_type === 'wp' ? 'special_pass' : 'stvp',
              stay_expiry_date: stayExpiryDate,
            },
          }, {
            onSuccess: () => {
              setCancellationDate('');
              setCancellationReference('');
              setStayExpiryDate('');
            },
            onSettled: () => setConfirmWorkPassCancellation(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmIR21Filing}
        theme="admin"
        title="Record Form IR21 filing?"
        message="This records evidence that Form IR21 was filed in myTax. It does not submit the form or release withheld monies."
        confirmLabel="Record filing"
        variant="primary"
        isLoading={recordIR21Filing.isPending}
        onCancel={() => setConfirmIR21Filing(false)}
        onConfirm={() => {
          recordIR21Filing.mutate({
            id: selected?.id,
            data: {
              submitted_date: ir21SubmittedDate,
              reference: ir21FilingReference.trim(),
              filing_type: 'original',
            },
          }, {
            onSuccess: () => {
              setIr21SubmittedDate('');
              setIr21FilingReference('');
            },
            onSettled: () => setConfirmIR21Filing(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmIR21Exemption}
        theme="admin"
        title="Record verified IR21 exemption?"
        message="Use this only when myTax or IRAS has confirmed that Form IR21 is not required. This closes IR21 and releases the payroll hold. Low processed YTD alone is not sufficient."
        confirmLabel="Record exemption"
        variant="primary"
        isLoading={recordIR21Exemption.isPending}
        onCancel={() => setConfirmIR21Exemption(false)}
        onConfirm={() => {
          recordIR21Exemption.mutate({
            id: selected?.id,
            data: {
              verified_date: ir21ExemptionDate,
              reference: ir21ExemptionReference.trim(),
              reason: ir21ExemptionReason.trim(),
            },
          }, {
            onSuccess: () => {
              setIr21ExemptionDate('');
              setIr21ExemptionReference('');
              setIr21ExemptionReason('');
            },
            onSettled: () => setConfirmIR21Exemption(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmIR21Directive}
        theme="admin"
        title="Record the verified IRAS outcome?"
        message={
          ir21VerifiedEvidence
            ? payrollEffectCopy({
              outcome: ir21VerifiedEvidence.outcome,
              amount: ir21VerifiedEvidence.amount,
              alreadyDisbursed: Boolean(
                ir21VerifiedEvidence.alreadyDisbursed
                || selected?.ir21_filing?.already_disbursed,
              ),
            }).body
            : 'Verify the uploaded IRAS letter before recording the outcome.'
        }
        confirmLabel="Record outcome"
        variant="primary"
        confirmDisabled={!canSubmitVerifiedDirective(ir21VerifiedEvidence)}
        isLoading={confirmIR21FromDocument.isPending}
        onCancel={() => setConfirmIR21Directive(false)}
        onConfirm={() => {
          if (!canSubmitVerifiedDirective(ir21VerifiedEvidence)) return;
          confirmIR21FromDocument.mutate({
            id: selected?.id,
            data: {
              document_id: ir21VerifiedEvidence.documentId,
              file_sha256: ir21VerifiedEvidence.fileSha256,
              outcome: ir21VerifiedEvidence.outcome,
              directive_date: ir21VerifiedEvidence.directiveDate,
              reference: ir21VerifiedEvidence.reference,
              amount: ir21VerifiedEvidence.outcome === 'pay_tax' ? ir21VerifiedEvidence.amount : undefined,
              due_date: ir21VerifiedEvidence.outcome === 'pay_tax' ? ir21VerifiedEvidence.dueDate : undefined,
              payment_slip_number: ir21VerifiedEvidence.paymentSlipNumber || '',
              document_type: ir21VerifiedEvidence.documentType,
              matches_document: true,
            },
          }, {
            onSuccess: () => {
              setIr21VerifiedEvidence(null);
              setIr21ReviewDocument(null);
            },
            onSettled: () => setConfirmIR21Directive(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmIR21Payment}
        theme="admin"
        title="Record payment to IRAS?"
        message={
          ir21PaidDate
            ? `Confirm that the directive amount was paid to IRAS on ${formatDate(ir21PaidDate)}.`
            : ''
        }
        confirmLabel="Record payment"
        variant="primary"
        isLoading={recordIR21Payment.isPending}
        onCancel={() => setConfirmIR21Payment(false)}
        onConfirm={() => {
          recordIR21Payment.mutate({
            id: selected?.id,
            data: { paid_date: ir21PaidDate },
          }, {
            onSuccess: () => setIr21PaidDate(''),
            onSettled: () => setConfirmIR21Payment(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmAgreedOffset}
        theme="admin"
        title="Apply the agreed employment end date?"
        message={
          agreedOffsetPreview
            ? `This changes the actual employment boundary to ${formatDate(agreedOffsetPreview.revised_end_date)}, after the ${agreedOffsetPreview.revised_end_session === 'AM' ? 'morning' : 'afternoon'} session, using ${formatLeaveDays(agreedOffsetPreview.proposed_offset_days)} days of AL. Leave extending beyond that boundary will be cancelled. The letter last day remains unchanged.`
            : ''
        }
        confirmLabel="Apply agreed date"
        variant="primary"
        isLoading={recordAgreedOffset.isPending}
        onCancel={() => setConfirmAgreedOffset(false)}
        onConfirm={() => {
          recordAgreedOffset.mutate({
            id: selected?.id,
            data: {
              revised_end_date: agreedOffsetPreview?.revised_end_date || agreedEndDate,
              revised_end_session: agreedOffsetPreview?.revised_end_session || agreedEndSession,
              process: true,
            },
          }, {
            onSuccess: () => {
              setAgreedEndDate('');
              setAgreedEndSession('PM');
              setAgreedOffsetDays('');
              setAgreedOffsetPreview(null);
            },
            onSettled: () => setConfirmAgreedOffset(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmClose}
        theme="admin"
        title="Close this offboarding case?"
        message="Required tasks must already be complete or waived. Employment records will be closed."
        confirmLabel="Close case"
        variant="danger"
        isLoading={closeCase.isPending}
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          closeCase.mutate(selected?.id, {
            onSettled: () => setConfirmClose(false),
          });
        }}
      />
      <ConfirmModal
        isOpen={confirmCutoff}
        theme="admin"
        title="Apply emergency access cutoff?"
        message="This immediately deactivates portal access. Use only when waiting until the last working day is not safe."
        confirmLabel="Cut off access"
        variant="danger"
        isLoading={emergencyCutoff.isPending}
        onCancel={() => setConfirmCutoff(false)}
        onConfirm={() => {
          emergencyCutoff.mutate(
            { id: selected?.id, data: { reason: cutoffReason } },
            { onSettled: () => setConfirmCutoff(false) },
          );
        }}
      />
      <IRASDirectiveVerificationModal
        isOpen={ir21VerificationOpen}
        onClose={() => setIr21VerificationOpen(false)}
        workflowId={selected?.id}
        employeeName={`${selected?.employee?.first_name || ''} ${selected?.employee?.last_name || ''}`.trim()}
        document={ir21ReviewDocument || selected?.ir21_filing?.pending_document}
        alreadyDisbursed={Boolean(selected?.ir21_filing?.already_disbursed)}
        onVerified={(evidence) => {
          setIr21VerifiedEvidence(evidence);
        }}
      />
    </div>
  );
};

export default OffboardingBoard;
